import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, Grid, Card, CardContent,
  Button, TextField, InputAdornment, IconButton, Chip, Divider, Tooltip, useTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Tabs, Tab,
} from '@mui/material';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StorageIcon from '@mui/icons-material/Storage';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SchoolIcon from '@mui/icons-material/School';
import BuildIcon from '@mui/icons-material/Build';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EventIcon from '@mui/icons-material/Event';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HubIcon from '@mui/icons-material/Hub';

import { localData } from '../services/localData';

const VPS_API = window.location.hostname.includes('localhost')
  ? 'http://localhost:3009'
  : '';

const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

const ECOSYSTEM_COLORS = {
  'ai-excellence-hub': '#002B45', signal: '#4A90D9', knowledge: '#2E5090',
  domino: '#C2B280', duplicates: '#7C3AED', 'data-insights': '#1a5276',
  'mirror-hub': '#8B7355', 'aph-oasis': '#2E86AB', 'aph-dashboard': '#D4A574',
  'applesandpears-biz': '#1a3c5e', 'n8n-workflows': '#FF6D00', 'lec': '#8B0040',
  'ini-community': '#10B981', 'tonishatagoe': '#9333EA', 'webcreatorstoolbox': '#0EA5E9',
  'aph-sales-bench': '#F59E0B'
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// ══════════════════════════════════════════════════════════════════════
// PROJECT DETAIL VIEW — drill-down with tabs
// ══════════════════════════════════════════════════════════════════════
const ProjectDetailView = ({ project, onBack }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projectData, setProjectData] = useState(null);
  const [error, setError] = useState(null);
  const [tableSearch, setTableSearch] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // Products data (for ai-excellence-hub)
  const [productsData, setProductsData] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);

  const isAIEx = project.name === 'ai-excellence-hub';
  const hasTables = (projectData?.tables || []).length > 0;
  const projectType = projectData?.type || 'unknown';
  const isTool = projectType === 'tool';
  const isApp = projectType === 'app';
  const isInfoOnly = (isTool || isApp || projectType === 'hub' || projectType === 'unknown') && !hasTables;

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = getToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const res = await fetch(`${VPS_API}/api/projects/${project.name}/data`, { headers, credentials: 'include' });
        if (res.ok) {
          setProjectData(await res.json());
        } else {
          setError('Failed to load project data');
        }
      } catch (e) {
        setError(e.message);
      }
      setLoading(false);
    };
    fetchDetail();
  }, [project.name]);

  // Fetch products data for AIEx
  useEffect(() => {
    if (!isAIEx) return;
    const fetchProducts = async () => {
      setProductsLoading(true);
      try {
        const token = getToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const res = await fetch(`${VPS_API}/api/products/data`, { headers, credentials: 'include' });
        if (res.ok) setProductsData(await res.json());
      } catch (e) { /* optional */ }
      setProductsLoading(false);
    };
    fetchProducts();
  }, [isAIEx]);

  const filteredTables = (projectData?.tables || []).filter(t =>
    !tableSearch || t.name.toLowerCase().includes(tableSearch.toLowerCase())
  );

  const color = ECOSYSTEM_COLORS[project.name] || '#002B45';

  // Products stats helper
  const pStats = productsData?.stats || {};
  const courses = productsData?.courses || [];
  const tools = productsData?.tools || [];
  const wcOrders = productsData?.wcOrders || [];
  const bootcamps = productsData?.bootcamps || [];
  const events = productsData?.events || [];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
        <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: color,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Typography sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 700 }}>
            {(project.displayName || project.name).charAt(0)}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            {project.displayName || project.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {project.description} &mdash; {project.tech}
          </Typography>
        </Box>
        <Tooltip title="Open externally">
          <IconButton onClick={() => window.open(project.devUrl, '_blank')}>
            <OpenInNewIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Project Info Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Status', value: project.status || 'unknown', chip: true },
          { label: 'Container', value: project.uptime || 'External' },
          { label: 'Disk', value: project.diskMB ? (project.diskMB >= 1024 ? `${(project.diskMB / 1024).toFixed(1)} GB` : `${project.diskMB} MB`) : '—' },
          { label: 'Port', value: project.port || '—' },
          { label: 'CPU', value: project.cpu || '—' },
          { label: 'Memory', value: project.memory?.split(' / ')[0] || '—' },
        ].map(info => (
          <Grid item xs={6} sm={4} md={2} key={info.label}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                {info.chip ? (
                  <Chip label={info.value} size="small" sx={{ bgcolor: info.value === 'live' ? '#e8f5e9' : '#fff3e0',
                    color: info.value === 'live' ? '#2e7d32' : '#e65100', fontWeight: 600, mb: 0.5 }} />
                ) : (
                  <Typography variant="h6" sx={{ fontWeight: 600, color, fontFamily: 'monospace' }}>{info.value}</Typography>
                )}
                <Typography variant="caption" color="text.secondary">{info.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Harvested data summary */}
      {project.data && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Harvested Data Summary</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.entries(project.data).filter(([k, v]) => k !== 'label' && k !== 'campaignNames' && typeof v === 'number' && v > 0).map(([k, v]) => (
              <Chip key={k} label={`${v} ${k}`} size="small" sx={{ bgcolor: `${color}10`, color, fontWeight: 500 }} />
            ))}
            {project.data.campaignNames?.map(cn => (
              <Chip key={cn} label={cn.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ borderColor: color, color }} />
            ))}
          </Box>
        </Paper>
      )}

      {/* Tabs — for projects with tables or AIEx */}
      {hasTables && (
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' } }}>
          <Tab label={`Database (${projectData?.tables?.length || 0})`} icon={<StorageIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          {isAIEx && <Tab label="Products & Sales" icon={<ShoppingCartIcon sx={{ fontSize: 18 }} />} iconPosition="start" />}
        </Tabs>
      )}

      {/* Loading / Error */}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ─── INFO VIEW for tools, apps, and projects without tables ── */}
      {!loading && projectData && isInfoOnly && (
        <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: 1.5, bgcolor: color, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {isTool ? <SettingsIcon /> : isApp ? <AutoAwesomeIcon /> : projectType === 'hub' ? <HubIcon /> : <InfoOutlinedIcon />}
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip label={projectType.charAt(0).toUpperCase() + projectType.slice(1)}
                  size="small" sx={{ bgcolor: `${color}18`, color, fontWeight: 600, fontSize: '0.72rem' }} />
              </Box>
              <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                {projectData.description || 'No description available.'}
              </Typography>
            </Box>
          </Box>

          {/* Capabilities */}
          {projectData.capabilities?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#002B45', fontSize: '0.85rem' }}>
                Capabilities
              </Typography>
              <Grid container spacing={1.5}>
                {projectData.capabilities.map((cap, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
                      border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: '#fafafa' }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>{cap}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Note */}
          {projectData.note && (
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f4f0', borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <InfoOutlinedIcon sx={{ fontSize: 18, color: '#999', mt: 0.2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem', lineHeight: 1.6 }}>
                  {projectData.note}
                </Typography>
              </Box>
            </Paper>
          )}
        </Paper>
      )}

      {/* ─── TAB 0: Data Tables ─────────────────────────────────────── */}
      {(activeTab === 0) && !loading && projectData && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Data Tables ({filteredTables.length})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {projectData.tables.reduce((s, t) => s + t.rowCount, 0).toLocaleString()} total rows across {projectData.tables.length} tables
              </Typography>
            </Box>
            <TextField size="small" placeholder="Filter tables..." value={tableSearch}
              onChange={e => setTableSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
              sx={{ width: 260 }} />
          </Box>

          {filteredTables.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
              <StorageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No data tables found.</Typography>
            </Paper>
          ) : (
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'background.default', '& th': { fontWeight: 700, fontSize: '0.75rem' } }}>
                    <TableCell>Table Name</TableCell>
                    <TableCell align="right">Rows</TableCell>
                    <TableCell align="right">Columns</TableCell>
                    <TableCell>Sample Columns</TableCell>
                    <TableCell align="right" sx={{ width: 60 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTables.map((table) => (
                    <TableRow key={table.name} hover
                      onClick={() => navigate(`/ecosystem/${project.name}/table/${table.name}`)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,43,69,0.03)' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StorageIcon sx={{ color, fontSize: 18 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{table.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={table.rowCount.toLocaleString()} size="small"
                          sx={{ bgcolor: table.rowCount > 0 ? `${color}10` : 'transparent',
                            color: table.rowCount > 0 ? color : 'text.disabled',
                            fontWeight: 600, fontSize: '0.72rem', minWidth: 50 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{table.columns.length}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {table.columns.slice(0, 5).map(col => (
                            <Chip key={col} label={col} size="small" variant="outlined"
                              sx={{ fontSize: '0.6rem', height: 18, borderColor: 'divider' }} />
                          ))}
                          {table.columns.length > 5 && (
                            <Chip label={`+${table.columns.length - 5}`} size="small"
                              sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'rgba(0,43,69,0.05)' }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <KeyboardArrowRightIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      {/* ─── TAB 1: Products & Sales (AIEx only) ────────────────────── */}
      {isAIEx && activeTab === 1 && (
        <>
          {productsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : !productsData ? (
            <Alert severity="info">Products data not available.</Alert>
          ) : (
            <>
              {/* Stats */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Courses', value: pStats.totalCourses || 0, sub: `${pStats.totalLessons || 0} lessons`, icon: SchoolIcon, color: '#002B45' },
                  { label: 'Tools', value: pStats.totalTools || 0, icon: BuildIcon, color: '#C2B280' },
                  { label: 'Bootcamps', value: pStats.totalBootcamps || 0, icon: EventIcon, color: '#00897b' },
                  { label: 'Events', value: pStats.totalEvents || 0, icon: EventIcon, color: '#ef6c00' },
                  { label: 'WC Orders', value: pStats.totalOrders || 0, sub: `${pStats.totalRegCodes || 0} reg codes`, icon: ShoppingCartIcon, color: '#7b1fa2' },
                ].map(s => (
                  <Grid item xs={6} sm={4} md key={s.label}>
                    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: s.color, color: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <s.icon fontSize="small" />
                        </Box>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#002B45', lineHeight: 1 }}>{s.value}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                          {s.sub && <Typography variant="caption" sx={{ display: 'block', color: '#999', fontSize: 10 }}>{s.sub}</Typography>}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Courses Table */}
              {courses.length > 0 && (
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: 3 }}>
                  <Box sx={{ p: 2, bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Courses ({courses.length})</Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {['Name', 'Academy', 'Category', 'Difficulty', 'Lessons', 'Status'].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f5f5f5' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {courses.slice(0, 50).map((c, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontSize: '0.76rem', fontWeight: 500 }}>{c.name}</TableCell>
                            <TableCell sx={{ fontSize: '0.72rem' }}>{c.academy || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.72rem' }}>{c.category || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.72rem' }}>
                              <Chip label={c.difficulty_level || '—'} size="small" sx={{ fontSize: '0.62rem', height: 20 }} />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.72rem' }}>{c.lesson_count || 0}</TableCell>
                            <TableCell sx={{ fontSize: '0.72rem' }}>
                              <Chip label={c.is_active ? 'Active' : 'Inactive'} size="small"
                                sx={{ fontSize: '0.62rem', height: 20, bgcolor: c.is_active ? '#e8f5e9' : '#fafafa', color: c.is_active ? '#2e7d32' : '#999' }} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {courses.length > 50 && (
                    <Box sx={{ p: 1, bgcolor: '#fafafa', textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Showing first 50 of {courses.length}</Typography>
                    </Box>
                  )}
                </Paper>
              )}

              {/* WooCommerce Orders Table */}
              {wcOrders.length > 0 && (
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: 3 }}>
                  <Box sx={{ p: 2, bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>WooCommerce Orders ({wcOrders.length})</Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {['Order #', 'Customer', 'Email', 'Product', 'Amount', 'Status', 'Date'].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f5f5f5' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {wcOrders.slice(0, 50).map((o, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>{o.order_id || o.id}</TableCell>
                            <TableCell sx={{ fontSize: '0.72rem' }}>{o.customer_name || o.billing_first_name || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.72rem' }}>{o.customer_email || o.billing_email || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.72rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {o.product_name || o.line_items || '—'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{o.total ? `$${o.total}` : '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.72rem' }}>
                              <Chip label={o.status || '—'} size="small"
                                sx={{ fontSize: '0.62rem', height: 20, bgcolor: o.status === 'completed' ? '#e8f5e9' : '#fff3e0',
                                  color: o.status === 'completed' ? '#2e7d32' : '#e65100' }} />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.72rem' }}>{o.date_created ? new Date(o.date_created).toLocaleDateString() : '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* Tools summary */}
              {tools.length > 0 && (
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: 3 }}>
                  <Box sx={{ p: 2, bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>AI Tools ({tools.length})</Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {['Name', 'Category', 'Pricing', 'Rating'].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f5f5f5' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tools.slice(0, 50).map((t, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontSize: '0.76rem', fontWeight: 500 }}>{t.name}</TableCell>
                            <TableCell sx={{ fontSize: '0.72rem' }}>{t.category || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.72rem' }}>{t.pricing_model || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.72rem' }}>{t.rating || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button variant="outlined" size="small" onClick={() => navigate(`/ecosystem/${project.name}/table/product`)}>
                  View all Products data
                </Button>
              </Box>
            </>
          )}
        </>
      )}
    </Container>
  );
};

// ══════════════════════════════════════════════════════════════════════
// PROJECTS PAGE — main table + drill-down
// ══════════════════════════════════════════════════════════════════════
const ProjectsPage = () => {
  const theme = useTheme();

  // Ecosystem projects from VPS
  const [ecosystemProjects, setEcosystemProjects] = useState([]);
  const [ecosystemLoading, setEcosystemLoading] = useState(true);

  // Drill-down state
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    const fetchEcosystem = async () => {
      setEcosystemLoading(true);
      try {
        const token = getToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const res = await fetch(`${VPS_API}/api/projects`, { headers, credentials: 'include' });
        if (res.ok) setEcosystemProjects((await res.json()) || []);
      } catch (err) { console.log('Could not fetch ecosystem:', err.message); }
      finally { setEcosystemLoading(false); }
    };
    fetchEcosystem();
  }, []);

  // ── Drill-down active ──
  if (selectedProject) {
    return <ProjectDetailView project={selectedProject} onBack={() => setSelectedProject(null)} />;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
      <Typography variant="h3" component="h1" gutterBottom sx={{
        fontFamily: "'Playfair Display', serif", borderBottom: `1px solid ${theme.palette.divider}`, pb: 2, mb: 4
      }}>
        APH Global
      </Typography>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3, justifyContent: 'center' }}>
        {[
          { label: 'Websites & Apps', value: ecosystemProjects.length, color: '#002B45', icon: HubIcon },
          { label: 'Live', value: ecosystemProjects.filter(p => p.status === 'live').length, color: '#2e7d32', icon: CheckCircleIcon },
          { label: 'Total Disk', value: ecosystemProjects.reduce((s, p) => s + (p.diskMB || 0), 0) >= 1024
              ? `${(ecosystemProjects.reduce((s, p) => s + (p.diskMB || 0), 0) / 1024).toFixed(1)} GB`
              : `${ecosystemProjects.reduce((s, p) => s + (p.diskMB || 0), 0)} MB`, color: '#1a5276', icon: StorageIcon },
          { label: 'Categories', value: new Set(ecosystemProjects.map(p => p.category)).size, color: '#C2B280', icon: AnalyticsIcon },
        ].map(s => (
          <Grid item xs={6} sm={3} md={2} key={s.label} sx={{ display: 'flex' }}>
            <Card elevation={0} sx={{ bgcolor: s.color, width: '100%', borderRadius: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, px: 2, py: 2 }}>
              <Box sx={{ width: 42, height: 42, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <s.icon sx={{ fontSize: 22, color: '#fff' }} />
              </Box>
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1 }}>{s.value}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>{s.label}</Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Ecosystem Quick-Link Cards — grouped by type */}
      {!ecosystemLoading && ecosystemProjects.length > 0 && (() => {
        const vpsApps = ecosystemProjects.filter(p => p.category !== 'Website');
        const wpSites = ecosystemProjects.filter(p => p.category === 'Website');
        const renderCards = (items) => items.map(ep => {
            const color = ECOSYSTEM_COLORS[ep.name] || '#002B45';
            return (
              <Grid item xs={6} sm={4} md={2} key={ep.name} sx={{ display: 'flex' }}>
                <Card elevation={0}
                  onClick={() => ep.type === 'external' ? window.open(ep.devUrl, '_blank') : setSelectedProject(ep)}
                  sx={{ width: '100%', height: '100%', cursor: 'pointer',
                    border: '1px solid', borderColor: 'divider', transition: 'all 0.15s',
                    display: 'flex', flexDirection: 'column',
                    '&:hover': { borderColor: color, boxShadow: `0 2px 8px ${color}20`, transform: 'translateY(-1px)' } }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 700 }}>
                        {(ep.displayName || ep.name).charAt(0)}
                      </Typography>
                    </Box>
                    <Box sx={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ep.displayName || ep.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.62rem', display: 'block', lineHeight: 1.2 }}>
                        {ep.status === 'live' ? 'Live' : ep.status || ''}{ep.tech ? ` \u00B7 ${ep.tech}` : ''}
                      </Typography>
                      {ep.data && (
                        <Typography variant="caption" sx={{ color, fontSize: '0.58rem', fontWeight: 600, display: 'block', lineHeight: 1.2, mt: 0.2 }}>
                          {ep.data.label || ''}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          });
        return (<>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 1.5 }}>VPS Apps & Platforms</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>{renderCards(vpsApps)}</Grid>
          {wpSites.length > 0 && (<>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 1.5 }}>WordPress Websites</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>{renderCards(wpSites)}</Grid>
          </>)}
        </>);
      })()}
      )}

      {/* Ecosystem Table */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: 4 }}>
        {ecosystemLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /><Typography sx={{ mt: 1 }}>Loading ecosystem...</Typography></Box>
        ) : ecosystemProjects.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No ecosystem projects found.</Typography></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default', '& th': { fontWeight: 700, fontSize: '0.75rem' } }}>
                  <TableCell>Project</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Container</TableCell>
                  <TableCell>Resources</TableCell>
                  <TableCell>Disk</TableCell>
                  <TableCell>Tech</TableCell>
                  <TableCell>Modified</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ecosystemProjects.map((ep) => (
                  <TableRow key={ep.name} hover onClick={() => setSelectedProject(ep)}
                    sx={{ cursor: 'pointer', '&:last-child td': { border: 0 }, '&:hover': { bgcolor: 'rgba(0,43,69,0.03)' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: ECOSYSTEM_COLORS[ep.name] || '#002B45',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 700 }}>
                            {(ep.displayName || ep.name).charAt(0)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.2 }}>
                            {ep.displayName || ep.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#1a5276', fontSize: '0.62rem' }}>
                            {ep.devUrl?.replace('https://', '')}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      {ep.data ? (
                        <Box>
                          {ep.data.label && <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem', color: '#002B45', display: 'block', lineHeight: 1.3 }}>{ep.data.label}</Typography>}
                          {ep.data.courses !== undefined && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                              {[{ k: 'lessons', v: ep.data.lessons }, { k: 'bootcamps', v: ep.data.bootcamps }, { k: 'events', v: ep.data.events },
                                { k: 'quizzes', v: ep.data.quizzes }, { k: 'users', v: ep.data.users }, { k: 'orders', v: ep.data.wcOrders }, { k: 'forms', v: ep.data.formSubmissions }
                              ].filter(x => x.v > 0).map(x => <Chip key={x.k} label={`${x.v} ${x.k}`} size="small" sx={{ fontSize: '0.58rem', height: 16, bgcolor: 'rgba(0,43,69,0.06)' }} />)}
                            </Box>
                          )}
                          {ep.data.campaignNames && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                              {ep.data.campaignNames.map(cn => <Chip key={cn} label={cn.replace(/_/g, ' ')} size="small" sx={{ fontSize: '0.58rem', height: 16, bgcolor: 'rgba(0,43,69,0.06)' }} />)}
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>{ep.description || '—'}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {ep.container ? (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 12, color: ep.uptime ? '#2e7d32' : '#999' }} />
                            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#2e7d32', fontWeight: 500 }}>{ep.uptime || 'unknown'}</Typography>
                          </Box>
                          <Typography variant="caption" sx={{ fontSize: '0.58rem', color: 'text.secondary', fontFamily: 'monospace' }}>:{ep.port || '—'}</Typography>
                        </Box>
                      ) : <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>External</Typography>}
                    </TableCell>
                    <TableCell>
                      {ep.cpu ? (
                        <Box>
                          <Typography variant="caption" sx={{ fontSize: '0.66rem', display: 'block', fontFamily: 'monospace' }}>CPU {ep.cpu}</Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.62rem', color: 'text.secondary', fontFamily: 'monospace' }}>{ep.memory?.split(' / ')[0] || '—'}</Typography>
                        </Box>
                      ) : <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: ep.diskMB > 500 ? 600 : 400 }}>
                        {ep.diskMB ? (ep.diskMB >= 1024 ? `${(ep.diskMB / 1024).toFixed(1)} GB` : `${ep.diskMB} MB`) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', display: 'block' }}>{ep.tech || '—'}</Typography>
                      <Chip label={ep.category || 'App'} size="small" variant="outlined"
                        sx={{ fontSize: '0.55rem', height: 18, mt: 0.3, borderColor: ECOSYSTEM_COLORS[ep.name] || '#002B45', color: ECOSYSTEM_COLORS[ep.name] || '#002B45' }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>{ep.lastModified ? formatDate(ep.lastModified) : '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="View data"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedProject(ep); }}><StorageIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        <Tooltip title="Open externally"><IconButton size="small" onClick={(e) => { e.stopPropagation(); window.open(ep.devUrl, '_blank'); }}><OpenInNewIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button component={Link} to="/data-projects" variant="outlined" size="large" startIcon={<AnalyticsIcon />}
          sx={{ mr: 2 }}>Data Projects</Button>
        <Button component={Link} to="/upload" variant="contained" color="primary" size="large" startIcon={<AddIcon />}>New Project</Button>
      </Box>
    </Container>
  );
};

export default ProjectsPage;
